import {
  Delivery,
  PickUp,
  Redelivery,
  Route,
} from 'src/graphql/types/delivery';
import { formatDollars } from 'src/components/InvoiceByDelivery/billing';
import {
  GOrderDeliveryMethodEnum,
  GProviderDispatchStatus,
  GThirdPartyProvider,
  GThirdPartyVehicleType,
} from 'src/graphql/types/generated';
import { todayYmd } from 'src/utils/date';

export type ThirdPartyStop = {
  route: Route;
  stop: Delivery | Redelivery;
  isRedelivery: boolean;
};

export const hasMetrobiOrder = (
  orders: { deliveryMethod: GOrderDeliveryMethodEnum }[] | undefined,
): boolean =>
  orders?.some(
    (order) =>
      order.deliveryMethod === GOrderDeliveryMethodEnum.MetrobiDelivery,
  ) ?? false;

export const isMetrobiRoute = (
  route: Route,
  incomingStops: (Delivery | Redelivery | PickUp)[] = [],
): boolean =>
  !route.isLobby &&
  // Keyed on the provider, not thirdPartyRoute: a route marked for Burq is
  // third_party too, and moving onto it must not raise Metrobi's eligibility
  // check. The Metrobi staging pen carries 'metrobi'.
  (route.thirdPartyProvider === GThirdPartyProvider.Metrobi ||
    (route.stops ?? []).some(
      (stop) => stop.__typename === 'Delivery' && hasMetrobiOrder(stop.orders),
    ) ||
    (route.redeliveryStops ?? []).some((stop) =>
      hasMetrobiOrder(stop.orders),
    ) ||
    incomingStops.some(
      (stop) => stop.__typename !== 'PickUp' && hasMetrobiOrder(stop.orders),
    ));

// A route is "fully Metrobi" when it has at least one delivery order and every
// order across all delivery + redelivery stops is metrobi_delivery. Vendor
// pickup stops are out of scope. This gates "Mark as Delivered": bulk-closing a
// route is only safe once no driver is capturing delivery on it. A stop mixing
// Metrobi and non-Metrobi orders counts as not-fully-Metrobi (require all
// orders). (sc-66450)
export const isFullyMetrobiRoute = (route: Route): boolean => {
  const orders = [
    ...(route.stops ?? []).flatMap((stop) =>
      stop.__typename === 'Delivery' ? stop.orders ?? [] : [],
    ),
    ...(route.redeliveryStops ?? []).flatMap((stop) => stop.orders ?? []),
  ];
  return (
    orders.length > 0 &&
    orders.every(
      (order) =>
        order.deliveryMethod === GOrderDeliveryMethodEnum.MetrobiDelivery,
    )
  );
};

type ProviderRouteShape = {
  thirdPartyRoute: boolean;
  bucketRoute: boolean;
  providerDispatch?: { status: GProviderDispatchStatus } | null;
};

// A route handed to a provider that Odeko dispatches by API: marked for it
// (third_party, not a holding pen) with a dispatch record. The record outlives
// an unmark (canceled) and a failed push (push_failed, route already generic),
// which is why the route-type terms are required, not just the record.
export const isApiDispatchedRoute = (route: ProviderRouteShape): boolean =>
  route.thirdPartyRoute &&
  !route.bucketRoute &&
  route.providerDispatch !== null &&
  route.providerDispatch !== undefined;

// A route a provider was handed that holds only redelivery stops: an
// escalation from the Issues page, or one reverted after its push failed.
// Mirrors LDS DeliveryRoute#provider_redelivery?; the dispatch record is what
// tells it from a GM's own route of redelivery stops, which has none.
export const isProviderRedeliveryRoute = (route: {
  stops?: readonly unknown[] | null;
  redeliveryStops?: readonly unknown[] | null;
  providerDispatch?: unknown;
}): boolean =>
  (route.redeliveryStops?.length ?? 0) > 0 &&
  (route.stops?.length ?? 0) === 0 &&
  route.providerDispatch !== null &&
  route.providerDispatch !== undefined;

// A GM-built route a provider is executing, whatever its dispatch state.
export const isProviderRoute = (route: ProviderRouteShape): boolean =>
  route.thirdPartyRoute && !route.bucketRoute;

// The per-night holding pen for a provider's stops: third_party and a bucket,
// never driven. The Lobby is a bucket too, hence the isLobby term.
export const isProviderPen = (route: {
  isLobby: boolean;
  thirdPartyRoute: boolean;
  bucketRoute: boolean;
}): boolean => !route.isLobby && route.thirdPartyRoute && route.bucketRoute;

// A dispatch the provider is running or about to run. Unmark is offered on
// one only after the provider canceled it, which the server's
// ProviderDispatch.unmarkable decides; the copy then says so.
export const isLiveDispatchStatus = (
  status: GProviderDispatchStatus | undefined,
): boolean =>
  status === GProviderDispatchStatus.Dispatched ||
  status === GProviderDispatchStatus.InProgress;

export const providerCanceledUnmarkMessage = (providerName: string): string =>
  `${providerName} canceled this route. It goes back to an Odeko route with its orders.`;

// Before a GM dispatches: the provider's route may exist, but no courier is
// booked. A row in one of these is the one a new mark would replace.
export const PRE_DISPATCH_STATUSES: readonly GProviderDispatchStatus[] = [
  GProviderDispatchStatus.Pushing,
  GProviderDispatchStatus.PushFailed,
  GProviderDispatchStatus.Quoting,
  GProviderDispatchStatus.QuoteFailed,
  GProviderDispatchStatus.Quoted,
];

// A provider quote as the wire carries it: every field but the id may be null.
export type ProviderQuote = {
  id: string;
  provider?: string | null;
  cost?: number | null;
  fee?: number | null;
  expiresAt?: string | null;
  // The class the quote was priced for; null when its request named none.
  vehicleType?: GThirdPartyVehicleType | null;
};

// A vehicle class to ask quotes for; null asks for any class, which Burq
// answers with every class, so the cheapest is usually a car.
export type VehicleChoice = GThirdPartyVehicleType | null;

// The order a GM reads them in, smallest first.
export const VEHICLE_TYPES: readonly GThirdPartyVehicleType[] = [
  GThirdPartyVehicleType.Car,
  GThirdPartyVehicleType.Suv,
  GThirdPartyVehicleType.PickupTruck,
  GThirdPartyVehicleType.CargoVan,
  GThirdPartyVehicleType.BoxTruck,
  GThirdPartyVehicleType.Truck_53Ft,
];

const VEHICLE_LABELS: Record<GThirdPartyVehicleType, string> = {
  [GThirdPartyVehicleType.Car]: 'Car',
  [GThirdPartyVehicleType.Suv]: 'SUV',
  [GThirdPartyVehicleType.PickupTruck]: 'Pickup truck',
  [GThirdPartyVehicleType.CargoVan]: 'Cargo van',
  [GThirdPartyVehicleType.BoxTruck]: 'Box truck',
  [GThirdPartyVehicleType.Truck_53Ft]: '53 ft truck',
};

export const ANY_VEHICLE_LABEL = 'Any vehicle';

// Null and absent (a quote from before classes were recorded) read the same:
// no class was asked for.
export const vehicleLabel = (
  vehicle: GThirdPartyVehicleType | null | undefined,
): string => (vehicle ? VEHICLE_LABELS[vehicle] ?? vehicle : ANY_VEHICLE_LABEL);

type QuotePrice = Pick<ProviderQuote, 'cost' | 'fee'>;

// What dispatching against a quote costs: the courier's price plus the
// provider's fee. Unknown while the provider has not priced it.
export const quoteTotalCents = (quote: QuotePrice): number | null =>
  quote.cost === null || quote.cost === undefined
    ? null
    : quote.cost + (quote.fee ?? 0);

export const quoteTotalLabel = (quote: QuotePrice): string => {
  const total = quoteTotalCents(quote);
  return total === null ? 'price pending' : formatDollars(total);
};

// The quote preselected for the GM: the lowest known total, the first on a
// tie. An unpriced quote is chosen only when no quote is priced.
export const cheapestQuote = <Quote extends QuotePrice>(
  quotes: readonly Quote[],
): Quote | undefined =>
  quotes.reduce<Quote | undefined>((best, quote) => {
    if (!best) return quote;
    const total = quoteTotalCents(quote);
    const bestTotal = quoteTotalCents(best);
    if (total === null) return best;
    return bestTotal === null || total < bestTotal ? quote : best;
  }, undefined);

// Cheapest first, unpriced last, the provider's order otherwise.
export const sortQuotes = <Quote extends QuotePrice>(
  quotes: readonly Quote[],
): Quote[] =>
  quotes
    .map((quote, index) => ({ quote, index, total: quoteTotalCents(quote) }))
    .sort((a, b) => {
      if (a.total === null || b.total === null) {
        if (a.total === b.total) return a.index - b.index;
        return a.total === null ? 1 : -1;
      }
      return a.total - b.total || a.index - b.index;
    })
    .map(({ quote }) => quote);

export const isQuoteExpired = (
  quote: Pick<ProviderQuote, 'expiresAt'>,
  now: number = Date.now(),
): boolean => !!quote.expiresAt && Date.parse(quote.expiresAt) <= now;

export const quoteProviderLabel = (
  quote: Pick<ProviderQuote, 'provider'>,
): string => quote.provider || 'Burq courier';

export const providerDisplayName = (provider: GThirdPartyProvider): string =>
  ({
    [GThirdPartyProvider.Burq]: 'Burq',
    [GThirdPartyProvider.Metrobi]: 'Metrobi',
  }[provider] ?? provider);

// Providers the DMP dispatches by API (quote, then dispatch); LDS refuses the
// API mutations for any other, which a GM marks by hand (Metrobi).
export const API_DISPATCHED_PROVIDERS: readonly GThirdPartyProvider[] = [
  GThirdPartyProvider.Burq,
];

// LDS caps a provider push at 50 stops (RouteProviderService::MAX_STOPS).
const MAX_PROVIDER_STOPS = 50;

// Whether LDS would accept marking this route for an API-dispatched provider,
// judged from what the route list carries: route_provider_service.rb
// validate_markable! and StopSetValidation. "Already underway" is not judged
// here (startTime is not on the route list); the mark reports it instead. So
// is a reverted escalation on a past night: LDS marks it again only while its
// redelivery stops are still open, which the route list does not carry, and
// refuses a stale one as an ended night.
export const isApiProviderOfferable = (
  route: Route & ProviderRouteShape & { redeliveryRoute: boolean },
  timeZone: string | undefined,
): boolean => {
  if (route.bucketRoute || route.thirdPartyRoute) return false;
  if (route.redeliveryRoute || route.customerPickupRoute || route.isLobby)
    return false;
  if (
    timeZone &&
    String(route.nightEnd) < todayYmd(timeZone) &&
    !isProviderRedeliveryRoute(route)
  )
    return false;
  const stops = route.stops ?? [];
  if (stops.some((stop) => stop.__typename === 'PickUp')) return false;
  const count = stops.length + (route.redeliveryStops ?? []).length;
  return count > 0 && count <= MAX_PROVIDER_STOPS;
};

export type MetrobiOffender = {
  stopId: string;
  locationId: string;
  businessName?: string | null;
  address?: string | null;
};

// Accept both the route-list and freshly fetched route-detail projections.
export const getMetrobiOffenders = (
  stops: {
    id: string;
    location?: {
      id: string;
      effectivelyExcluded: boolean;
      businessName: string;
      displayAddress: string;
    };
  }[],
): MetrobiOffender[] =>
  stops.flatMap(({ id, location }) =>
    location?.effectivelyExcluded
      ? [
          {
            stopId: id,
            locationId: location.id,
            businessName: location.businessName,
            address: location.displayAddress,
          },
        ]
      : [],
  );

// A batch of marked orders can turn an ordinary destination into a Metrobi
// route, so its existing stops must also be eligible at that transition.
export const getMetrobiMoveOffenders = (
  destination: Route,
  incomingStops: (Delivery | Redelivery | PickUp)[],
): MetrobiOffender[] => {
  if (!isMetrobiRoute(destination, incomingStops)) return [];
  const stops = isMetrobiRoute(destination)
    ? incomingStops
    : [
        ...(destination.stops ?? []),
        ...(destination.redeliveryStops ?? []),
        ...incomingStops,
      ];
  const uniqueStops = new Map(
    stops
      .filter((stop) => stop.__typename !== 'PickUp')
      .map((stop) => [`${stop.__typename}:${stop.id}`, stop]),
  );
  return getMetrobiOffenders([...uniqueStops.values()]);
};

// A 3PD stop is any stop with at least one metrobi_delivery order, wherever it
// currently sits (the never-optimized bucket route, or its original route if
// hand-marked and never auto-bucketed). Not sourced from route membership: a
// hand-marked order is never physically moved to the bucket route, so
// route-membership alone would miss it.
export const findThirdPartyStops = (routes: Route[]): ThirdPartyStop[] => {
  const thirdPartyStops: ThirdPartyStop[] = [];

  routes.forEach((route) => {
    (route.stops ?? []).forEach((stop) => {
      if (stop.__typename !== 'Delivery') return;
      if (hasMetrobiOrder(stop.orders)) {
        thirdPartyStops.push({ route, stop, isRedelivery: false });
      }
    });
    (route.redeliveryStops ?? []).forEach((stop) => {
      if (hasMetrobiOrder(stop.orders)) {
        thirdPartyStops.push({ route, stop, isRedelivery: true });
      }
    });
  });

  return thirdPartyStops;
};

export type ThirdPartyRouteGroup = {
  route: Route;
  stops: ThirdPartyStop[];
};

// The Metrobi sub-tab's rows under a heading per route, in the order the
// routes come (the pen first, then the sorted list). A hand-marked stop sits
// on its own route, so a route can hold Metrobi stops without being the pen.
export const groupThirdPartyStopsByRoute = (
  thirdPartyStops: ThirdPartyStop[],
): ThirdPartyRouteGroup[] => {
  const groups: ThirdPartyRouteGroup[] = [];

  thirdPartyStops.forEach((thirdPartyStop) => {
    const group = groups.find(
      (candidate) => candidate.route.id === thirdPartyStop.route.id,
    );
    if (group) group.stops.push(thirdPartyStop);
    else groups.push({ route: thirdPartyStop.route, stops: [thirdPartyStop] });
  });

  return groups;
};
